import { useCallback, useEffect, useLayoutEffect } from 'react';
import { isEmpty, isNil, some, values } from 'lodash';
import { useCurrentValueGetter } from 'hooks';
import { useTracerContext } from 'modules/taboola-common-frontend-modules/tracer/TracerProvider';
import { SPAN_SCOPE } from 'modules/taboola-common-frontend-modules/tracer/config/spanScopes';
import { useIsTracerPermitted } from 'modules/taboola-common-frontend-modules/tracer/hooks/useIsTracerPermitted';
import { TRACER_STATUS_CODE } from 'modules/taboola-common-frontend-modules/tracer/utils/statusCode';
import { COMPONENT_STATUS } from 'services/constants';
import { gridMeasurement } from '../utils/gridMeasurement';

export const SPAN_EVENT_TYPES = {
    GRID_MEASUREMENT: 'GRID_MEASUREMENT',
    GRAPH_MEASUREMENT: 'GRAPH_MEASUREMENT',
};

const requiredEvents = [SPAN_EVENT_TYPES.GRAPH_MEASUREMENT, SPAN_EVENT_TYPES.GRID_MEASUREMENT];
const MEASURE_EXPIRATION_TIME = 110000;

export const useReportTracer = ({
    graphDataStatus,
    reportDataStatus,
    reportData,
    graphData,
    selectedReport,
    activeReportFilters,
    startDate,
    endDate,
}) => {
    const isTracerPermitted = useIsTracerPermitted();

    const { createRootSpan, getRootSpan, endRootSpan, startSpan, childSpansRef, getSpan, endSpan, setSpanStatus } =
        useTracerContext();
    const selectedReportGetter = useCurrentValueGetter(selectedReport);

    const endSpanHandler = useCallback(() => {
        if (!isTracerPermitted) {
            return;
        }
        const activeSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        if (!activeSpan) {
            return;
        }

        const measureValues = values(childSpansRef.current);
        const notCompleteAllMeasures = some(measureValues, span => requiredEvents.includes(span.name));

        if (!notCompleteAllMeasures) {
            endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        }
    }, [childSpansRef, endRootSpan, getRootSpan, isTracerPermitted]);

    useLayoutEffect(() => {
        if (!isTracerPermitted) {
            return;
        }
        //start measure
        const rootSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        const { selectedReport } = rootSpan?.attributes || {};
        if (!isNil(selectedReport) && selectedReport !== selectedReportGetter()) {
            setSpanStatus(rootSpan, TRACER_STATUS_CODE.UNSET);
            endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        }
        if (
            isNil(rootSpan) &&
            (reportDataStatus === COMPONENT_STATUS.INITIAL ||
                graphDataStatus === COMPONENT_STATUS.INITIAL ||
                graphDataStatus === COMPONENT_STATUS.LOADING)
        ) {
            const span = createRootSpan(SPAN_SCOPE.REPORT_SCOPE, {
                attributes: {
                    selectedReport: selectedReportGetter(),
                    activeReportFilters: JSON.stringify(activeReportFilters),
                    startDate: JSON.stringify(startDate),
                    endDate: JSON.stringify(endDate),
                },
            });

            const measureProps = {
                eventType: SPAN_EVENT_TYPES.GRID_MEASUREMENT,
                selectedReport: selectedReportGetter(),
                endSpanHandler,
                endSpan,
            };
            gridMeasurement(measureProps);

            startSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT, span, {
                selectedReport: selectedReportGetter(),
            });
            const timeoutId = setTimeout(() => {
                setSpanStatus(span, TRACER_STATUS_CODE.ERROR, 'Timeout');
                console.log('Tracer Timeout');
                endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
            }, MEASURE_EXPIRATION_TIME);
            return () => clearTimeout(timeoutId);
        }
    }, [
        getRootSpan,
        reportDataStatus,
        graphDataStatus,
        createRootSpan,
        selectedReportGetter,
        endSpanHandler,
        endRootSpan,
        startSpan,
        endSpan,
        activeReportFilters,
        setSpanStatus,
        isTracerPermitted,
        getSpan,
        startDate,
        endDate,
    ]);

    useEffect(() => {
        if (!isTracerPermitted) {
            return;
        }
        // add graph measure
        const activeSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        if (graphDataStatus === COMPONENT_STATUS.INITIAL || graphDataStatus === COMPONENT_STATUS.LOADING) {
            startSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT, activeSpan, {
                selectedReport: selectedReportGetter(),
            });
        }

        if (
            graphDataStatus === COMPONENT_STATUS.ACTIVE ||
            graphDataStatus === COMPONENT_STATUS.ERROR ||
            graphDataStatus === COMPONENT_STATUS.EMPTY
        ) {
            const activeSpan = getSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
            if (graphDataStatus === COMPONENT_STATUS.ERROR) {
                setSpanStatus(activeSpan, TRACER_STATUS_CODE.ERROR, `Graph data status: ${graphDataStatus}`);
            }
            if (isEmpty(graphData)) {
                setSpanStatus(activeSpan, TRACER_STATUS_CODE.UNSET, 'No available Graph Data');
            }
            endSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
            endSpanHandler();
        }
    }, [
        endSpan,
        endSpanHandler,
        getRootSpan,
        getSpan,
        graphDataStatus,
        selectedReportGetter,
        setSpanStatus,
        startSpan,
        isTracerPermitted,
        graphData,
    ]);

    useEffect(() => {
        if (!isTracerPermitted) {
            return;
        }
        //stop grid measure
        const activeSpan = getSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
        if (reportDataStatus === COMPONENT_STATUS.ACTIVE) {
            if (isEmpty(reportData)) {
                setSpanStatus(activeSpan, TRACER_STATUS_CODE.UNSET, 'No available Report Data');
                endSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
            }
            endSpanHandler();
        }
        if (reportDataStatus === COMPONENT_STATUS.ERROR || reportDataStatus === COMPONENT_STATUS.EMPTY) {
            setSpanStatus(activeSpan, TRACER_STATUS_CODE.ERROR, `Report data status: ${reportDataStatus}`);
            endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        }
    }, [
        reportDataStatus,
        selectedReportGetter,
        endRootSpan,
        getRootSpan,
        endSpanHandler,
        graphDataStatus,
        setSpanStatus,
        getSpan,
        isTracerPermitted,
        reportData,
        endSpan,
    ]);
};
